import Cookies from 'universal-cookie';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { setUser, getUser } from 'Utils/UserCookie';
import { device } from 'device.js';
import { trackEvent as segmentTrackEvent, TRACKING_ENUMS } from 'Utils/Analytics/Segment';
import {
  createEntitlementCookie,
  createMvpdSubscriptionCookie,
  removeEntitlementCookie,
  removeMvpdSubscriptionCookie,
} from 'Utils/Entitlements';
import { trackPurchaseSource } from 'State/Tracking';
import { getDefaultConfig } from 'Utils/Store';
import { login } from 'Api/ProfileApi';
import { createAuthInstance } from 'Utils/AuthSDK';
import {
  checkAuthenticationRequest,
  getAuthentication,
  getMvpdEntitlements,
  getUserMetadata,
  logoutOfMVPD,
} from 'Api/MVPDAuthApi';
import { createProfile } from './ProfileSDK';

const cookies = new Cookies();

export const MVPD_COOKIE_NAME = 'mvpd-auth';

export const getDeviceProps = () => {
  let model = '';
  let osName = '';

  if (device.desktop) {
    model = 'Desktop';
  } else if (device.tablet) {
    model = 'Tablet';
  } else {
    model = 'Mobile phone';
  }

  if (device.windows) {
    osName = 'Windows';
  } else if (device.ios) {
    osName = 'iOS';
  } else if (device.android) {
    osName = 'Android';
  } else {
    osName = 'Other';
  }

  return { model, osName };
};

export const getAuthenticationConfig = (opts = {}) => {
  const { rootDomain, deviceIdCookieName } = getDefaultConfig();
  const { mvpdId } = opts;
  const { model, osName } = getDeviceProps();
  const deviceId = cookies.get(deviceIdCookieName) || null;
  const redirectUrl = `${rootDomain}/static/html/auth_success_landing.html`;
  return {
    appVersion: 'web',
    deviceId,
    deviceType: 'web',
    firstScreen: true,
    model,
    mvpdId,
    osName,
    redirectUrl,
    requestor: 'fbc-fox',
  };
};

export const checkAuthentication = async (options = {}) => {
  try {
    const result = await checkAuthenticationRequest(options);
    return result;
  } catch (error) {
    return null;
  }
};

export const checkAuthenticationOnWindowFocus = async (options = {}) =>
  new Promise(async (resolve, reject) => {
    let documentFocusTimeout;
    const listener = async () => {
      if (!document.hasFocus()) return;
      clearInterval(documentFocusTimeout);
      try {
        const authResult = await checkAuthentication(options);
        resolve(authResult);
      } catch (e) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject(null);
      }
    };
    documentFocusTimeout = setInterval(listener, 1000);
  });

export const mvpdLogout = async () => {
  const logoutResponse = await logoutOfMVPD();
  if (logoutResponse) {
    cookies.remove(MVPD_COOKIE_NAME, { path: '/' });
    removeEntitlementCookie();
    removeMvpdSubscriptionCookie();
    trackPurchaseSource();
  }
  return logoutResponse || null;
};

export const handleSignOut = async ({ profileSignOut }) => {
  const userData = getUser();
  if (userData.mvpd) {
    const logoutResponse = await mvpdLogout();
    if (logoutResponse) await setUser(logoutResponse);
  }
  await profileSignOut();
};

export const openMvpdWindow = () => window.open('', 'mvpdwindow', null, true);

export const closeMvpdWindow = (window) => {
  if (window) window.close();
};

export const openProviderSignInWindow = async (authenticationConfig) => {
  const mvpdWindow = openMvpdWindow();
  const authenticationData = await getAuthentication(authenticationConfig);
  let authenticationUrl = get(authenticationData, 'authenticateURL');
  if (!authenticationUrl) {
    closeMvpdWindow(mvpdWindow);
    throw new Error('No authenticateURL to open mvpd sign in window');
  }
  const deviceInfo = get(authenticationData, 'deviceInfo');
  if (deviceInfo) {
    authenticationUrl = `${authenticationUrl}&device_info=${deviceInfo}`;
  }
  mvpdWindow.location = authenticationUrl;
  return mvpdWindow;
};

export const getUserData = async ({ authenticationConfig, mvpdWindow }) => {
  const authResult = await checkAuthenticationOnWindowFocus(authenticationConfig);
  closeMvpdWindow(mvpdWindow);
  if (!authResult) throw new Error('User not successfully sign into mvpd');
  const accessToken = get(authResult, 'accessToken');
  createAuthInstance(accessToken); //getUserMetadata method used below needs access token
  const metadata = await getUserMetadata();
  const { userID } = metadata;
  const { deviceId, mvpdId } = authenticationConfig;
  const payload = { deviceId, mvpd: mvpdId, mvpdHash: userID };
  await login(payload);
  // This login allow us to associate a non-anon profile with the user
  // and preserve information like bookmarks and favorites across sessions
  const preauthorizedResources = await getMvpdEntitlements();
  return {
    ...authResult,
    metadata,
    preauthorizedResources,
  };
};

export const handleMvpdSignIn = async (opts) => {
  const { entitlementId, hideOverlay, mvpdId, mvpdName } = opts;
  let isEntitled = false;
  let signInComplete = false;
  let userData;
  try {
    segmentTrackEvent(TRACKING_ENUMS.EVENT_PARTNER_SIGN_IN_STARTED, {
      page_start_source: 'general',
      page_profile_type: mvpdName,
    });
    segmentTrackEvent(TRACKING_ENUMS.EVENT_AUTH_STARTED, {
      page_start_source: 'general',
    });
    createAuthInstance();
    const authenticationConfig = getAuthenticationConfig({ mvpdId });
    const mvpdWindow = await openProviderSignInWindow(authenticationConfig);
    userData = await getUserData({ authenticationConfig, mvpdWindow });
    const { preauthorizedResources } = userData;
    isEntitled = includes(preauthorizedResources, entitlementId);
    if (isEntitled) {
      await setUser(userData);
      trackPurchaseSource(mvpdName);
      createEntitlementCookie();
      createMvpdSubscriptionCookie();
    } else {
      trackPurchaseSource();
      removeEntitlementCookie();
      removeMvpdSubscriptionCookie();
      hideOverlay();
    }
    createProfile();
    segmentTrackEvent(TRACKING_ENUMS.EVENT_AUTH_COMPLETED, {
      page_start_source: 'general',
    });
    segmentTrackEvent(TRACKING_ENUMS.EVENT_PARTNER_SIGN_IN_COMPLETED, {
      page_start_source: 'general',
      page_profile_type: mvpdName,
    });
    segmentTrackEvent(TRACKING_ENUMS.EVENT_LOGIN_COMPLETE, {
      page_start_source: 'general',
      page_profile_type: mvpdName,
    });
    signInComplete = true;
  } catch (err) {
    console.debug('MVPD Sign In Error:', err);
    segmentTrackEvent(TRACKING_ENUMS.EVENT_PARTNER_SIGN_IN_ERROR, {
      page_start_source: 'general',
      page_profile_type: mvpdId,
      page_error_description: get(err, 'message'),
      page_error_type: 'server-side',
    });
    segmentTrackEvent(TRACKING_ENUMS.EVENT_LOGIN_ERROR, {
      page_start_source: 'general',
      page_error_description: get(err, 'message'),
      page_error_type: 'server-side',
    });
    hideOverlay();
  }
  return { isEntitled, signInComplete, userData };
};
